/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import ReactTooltip from 'react-tooltip';
import Card from './Card';
import Input, { AddressInput, PhoneNumberInput } from './Input';
import {
  GET_CURRENT_ORGANIZATION,
  GET_TIMEZONES,
  UPDATE_ORGANIZATION_INFO,
  UPLOAD_FILES,
} from '../graphql/queries';
import Loader from './Loader';
import colors from '../styles/colors';
import Button from './Button';
import { AppContext } from '../context/AppContext';

const LoaderContainer = styled.div`
  position: absolute;
  top: calc(50vh - 60px);
  left: calc(50vw - 60px);
`;

const Form = styled.form`
  width: 70%;
  overflow-y: auto;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const Image = styled.div`
  margin-top: 30px;
  background-image: ${(props) => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  background-origin: unset;
  border-radius: ${(props) => props.radius || '5px'};
  height: 190px;
  width: 300px;
`;

const Logo = styled.div`
  margin-top: 30px;
  max-width: 100px;
  height: 100px;
  width: 100px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid ${colors.grey5};
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ImageContainer = styled.img`
  width: 100%;
  height: auto;
`;

const DropzoneText = styled.div`
  margin-top: 15px;
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.seaGreen};
  text-decoration: underline;
  cursor: pointer;
`;

const DropzoneContainer = styled.div`
  width: fit-content;
`;

const DropdownHeader = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 30px 0px 10px 0px;
`;

const DropdownContainer = styled.div`
  font-size: 1.3rem;
  width: 100%;
`;

const InstructionsText = styled.p`
  padding: 15px 0;
  color: ${colors.grey3};
  font-size: 11px;
`;

export default function BusinessProfile() {
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhoneNumber, setOwnerPhoneNumber] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [timeZoneObj, setTimeZoneObj] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [posterImageUrl, setPosterImageUrl] = useState('');
  const [isLogoUpload, setIsLogoUpload] = useState(false);
  const [isPosterUpload, setIsPosterUpload] = useState(false);
  const { data, loading } = useQuery(GET_CURRENT_ORGANIZATION);
  const { setApp } = useContext(AppContext);
  const { data: timezonesData } = useQuery(GET_TIMEZONES);
  const [uploadFiles] = useMutation(UPLOAD_FILES, {
    onError(error) {
      console.log(error);
    },
    onCompleted(data) {
      if (isLogoUpload) {
        setLogoUrl(data.uploadFiles[0].url);
        setIsLogoUpload(false);
      }
      if (isPosterUpload) {
        setPosterImageUrl(data.uploadFiles[0].url);
        setIsPosterUpload(false);
      }
    },
  });

  const [updateOrganizationInfo, { loading: updateLoading }] = useMutation(
    UPDATE_ORGANIZATION_INFO,
    {
      variables: {
        name,
        ownerName,
        ownerEmail,
        ownerPhoneNumber,
        address: {
          address1,
          address2,
          city,
          state,
          zip,
        },
        logoUrl,
        posterImageUrl,
        timezoneId: timeZoneObj?.value,
      },
      onCompleted(data) {
        console.log(data.updateOrganizationInfo.timezone.label);
        const orgTimeZone = data?.updateOrganizationInfo?.timezone.label;
        setApp((app) => ({ ...app, orgTimeZone }));
      },
    },
  );
  const onDrop = React.useCallback(
    (acceptedFiles) => {
      uploadFiles({
        variables: {
          files: acceptedFiles,
        },
      });
    },
    [uploadFiles],
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
  });

  useEffect(() => {
    if (data?.getCurrentOrganization && timezonesData?.timezones) {
      console.log(data.getCurrentOrganization);
      const {
        getCurrentOrganization,
        getCurrentOrganization: { address },
      } = data;
      let timeZone;

      if (data.getCurrentOrganization.timezone !== null) {
        timeZone = data.getCurrentOrganization.timezone;
      } else {
        timeZone = timezonesData.timezones.defaultTimezone;
      }

      setName(getCurrentOrganization.name);
      setOwnerName(getCurrentOrganization.ownerName);
      setOwnerEmail(getCurrentOrganization.ownerEmail);
      setOwnerPhoneNumber(getCurrentOrganization.ownerPhoneNumber);
      setLogoUrl(getCurrentOrganization.logoUrl);
      setPosterImageUrl(getCurrentOrganization.posterImageUrl);
      setAddress1(address.address1);
      setAddress2(address.address2);
      setCity(address.city);
      setState(address.state);
      setZip(address.zip);
      setTimeZoneObj(timeZone);
    }
  }, [data, timezonesData]);

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        updateOrganizationInfo();
      }}
    >
      <Card width="100%" padding="20px 30px">
        <Input
          top
          width="100%"
          header="BUSINESS NAME"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Input
          margin="15px 0px 0px 0px"
          top
          width="100%"
          header="OWNER NAME"
          type="text"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          required
        />
        <Input
          margin="15px 0px 15px 0px"
          top
          type="email"
          header="OWNER EMAIL"
          value={ownerEmail}
          onChange={(e) => setOwnerEmail(e.target.value)}
          required
          width="100%"
         />
        <PhoneNumberInput
          top
          type="tel"
          header="OWNER PHONE NUMBER"
          value={ownerPhoneNumber}
          onChange={(e) => setOwnerPhoneNumber(e.target.value)}
          width="calc(100%)"
          required
              />
        <AddressInput
          width="100%"
          header="ADDRESS"
          address1Value={address1}
          address1OnChange={(e) => setAddress1(e.target.value)}
          address2Value={address2}
          address2OnChange={(e) => setAddress2(e.target.value)}
          cityValue={city}
          cityOnChange={(e) => setCity(e.target.value)}
          stateValue={state}
          stateOnChange={(e) => setState(e.target.value)}
          zipValue={zip}
          zipOnChange={(e) => setZip(e.target.value)}
        />
        <DropdownHeader>Time Zone</DropdownHeader>
        <DropdownContainer>
          <Select
            defaultValue={timeZoneObj}
            value={timeZoneObj}
            onChange={(val) => {
              setTimeZoneObj({ ...val });
            }}
            options={timezonesData?.timezones?.timezonesArray}
          />
        </DropdownContainer>

        <Logo data-tip="200x200px">
          <ImageContainer src={logoUrl} />
        </Logo>

        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <DropzoneText
            onClick={() => {
              setIsLogoUpload(true);
              setIsPosterUpload(false);
            }}
          >
            Change Logo
          </DropzoneText>
        </DropzoneContainer>
        <InstructionsText>
          <span style={{ fontWeight: '500', color: 'black' }}>
            Recommendation:{' '}
          </span>
          Upload a Logo with 200x200px dimensions.
        </InstructionsText>

        <Image data-tip="Max 1080px wide" src={posterImageUrl} />
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <DropzoneText
            onClick={() => {
              setIsLogoUpload(false);
              setIsPosterUpload(true);
            }}
          >
            Change Poster Image
          </DropzoneText>
        </DropzoneContainer>
        <InstructionsText>
          <span style={{ fontWeight: '500', color: 'black' }}>
            Recommendation:{' '}
          </span>{' '}
          Upload a cover image within 1080px wide & 3MB in size.{' '}
        </InstructionsText>
      </Card>
      <Button
        margin="40px 30px"
        text="UPDATE BUSINESS PROFILE"
        type="submit"
        width={updateLoading ? '50%' : 'fit-content'}
        loading={updateLoading}
      />
      <ReactTooltip place="right" type="dark" effect="solid" />
    </Form>
  );
}
